import { inject, Injectable } from '@angular/core';
import { Auth, createUserWithEmailAndPassword, signInWithEmailAndPassword, updatePassword } from '@angular/fire/auth';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { ENVIRONMENT } from '../core.constants';
import { TranslocoService } from '@ngneat/transloco';
import { BehaviorSubject, filter, from, map, Observable, OperatorFunction, tap } from 'rxjs';

export interface AuthStatus {
  // Undefined while checking
  loggedIn: boolean | undefined;
  uid?: string;
}

const EMPTY_LOGGED = { loggedIn: undefined };

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private functions = inject(Functions);
  private translocoService = inject(TranslocoService);
  private environment = inject(ENVIRONMENT);

  private authStatusSubject = new BehaviorSubject<AuthStatus>(EMPTY_LOGGED);
  public authStatus$ = this.authStatusSubject.asObservable();

  public isLoggedIn$: Observable<boolean> = this.authStatusSubject.pipe(
    filter((x) => x != null),
    map((v) => Boolean(v.loggedIn)),
  );

  /**
   * Returns loggedIn user id. After logout it does not change until next login.
   */
  public uid$: Observable<string> = this.authStatusSubject.pipe(
    filter((status) => status.loggedIn === true && status.uid !== undefined) as OperatorFunction<
      AuthStatus,
      Required<AuthStatus>
    >,
    map((v) => v.uid),
  );

  constructor(private auth: Auth) {
    this.auth.onAuthStateChanged((user) => {
      if (user) {
        this.authStatusSubject.next({ loggedIn: true, uid: user.uid });
      } else {
        this.authStatusSubject.next({ loggedIn: false });
      }
    });
  }

  login(email: string, pass: string) {
    this.authStatusSubject.next(EMPTY_LOGGED);
    return signInWithEmailAndPassword(this.auth, email.trim(), pass);
  }

  logout() {
    return from(this.auth.signOut()).pipe(tap(() => this.authStatusSubject.next({ loggedIn: false })));
  }

  register(email: string, pass: string) {
    this.authStatusSubject.next(EMPTY_LOGGED);
    return createUserWithEmailAndPassword(this.auth, email.trim(), pass);
  }

  resetPassword(email: string): Promise<void> {
    const lang = this.translocoService.getActiveLang();
    const callable = httpsCallable(this.functions, 'resetUserPasswordSendEmail');
    return callable({ email: email.trim(), appType: this.environment.appType, language: lang }).then((result: any) => result.data);
  }

  updatePassword(newPassword: string) {
    const currentUser = this.auth.currentUser;
    if (currentUser) {
      return updatePassword(currentUser, newPassword);
    } else {
      console.error('Current user is null. Unable to update password.');
      return false;
    }
  }
}
